import PositionMenu from "@/Washouts/assets/icons/sideBarMenu.svg";
import PositionMenuSelected from "@/Washouts/assets/icons/sideBarMenuSelected.svg";
import { BackdropClassKey, Fade, IconButton, Menu, MenuItem, Stack, Typography } from "@mui/material";
import { TreeIcon } from "../PositionTreeIcon";
import { clsx } from "clsx";
import { useMemo, useState } from "react";
import { cloneDeep } from "lodash";
import { IList, IWashoutQueries } from "@/Washouts/types/WashoutReview";
import { useFactoryWashout } from "@/Washouts/hooks/useFactoryWashout";
import { normalizePositions } from "@/Common/helpers/Positions";
import { useWashoutReviewActions } from "@/Washouts/hooks/WashoutReview/useWashoutReviewActions";
import { evaluatePositionStatusConditions, StatusKey } from "@/Washouts/helpers/Statuses";
import { or } from "@/Common/helpers/sonar";

interface PositionTreeMenuProps {
	disabled?: boolean;
	shouldShowMenu?: boolean;
	classes?: string;
	selected?: boolean;
	locationId: string;
	currentUser: string;
	position: string;
	item: IList;
	itemProps?: {
		selected: boolean;
		reviewed: boolean;
		currentPayPlanAssignmentId: string;
		hasWashout: boolean;
		upcomingPayPlanAssignmentId: string;
		washoutGenerationError: boolean;
	};
	unReviewable?: boolean;
	disabledMenuAction: {
		shouldDisabledApproveWashoutMenu: boolean;
		shouldDisabledRetractWashoutMenu: boolean;
		shouldDisabledGenerateWashoutMenu: boolean;
	};
}

interface PositionTreeMenuActionsProps {
	anchorEl?: null | HTMLElement;
	setAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
	menuActions?: Record<string, { disabled: boolean; handleOnClick: () => void }>;
}

type TUpdateWashoutStatusMutation = (input: {
	status: {
		status: string;
		statusSetBy: string | undefined;
		statusSetOn: Date;
	};
}) => void;

type TMenuAction = {
	isSelected: boolean;
	statusKey: StatusKey;
	item: IList;
	locationId: string;
	currentUser: string;
	updateWashoutStatusMutation: TUpdateWashoutStatusMutation;
	isPendingUpdateWashout: boolean;
	generateWashoutMutation: () => void;
	isPendingGenerateWashout: boolean;
	disabledMenuAction: {
		shouldDisabledApproveWashoutMenu: boolean;
		shouldDisabledRetractWashoutMenu: boolean;
		shouldDisabledGenerateWashoutMenu: boolean;
	};
};

enum MenuActionStatusKey {
	ApproveWashout = "approve-washout",
	RetractApprovalWashout = "retract-approval",
	GenerateWashout = "generate-washout",
}

export const MenuActions = {
	[MenuActionStatusKey.ApproveWashout]: { disabled: false, handleOnClick: () => {} },
	[MenuActionStatusKey.RetractApprovalWashout]: { disabled: false, handleOnClick: () => {} },
	[MenuActionStatusKey.GenerateWashout]: { disabled: false, handleOnClick: () => {} },
};

const ICON_SX = { minWidth: 16, minHeight: 16, maxWidth: 16, maxHeight: 16 };

const defineInputToApprovement = (params: TMenuAction, status: string) => {
	const { currentUser } = params;

	return {
		status: {
			status,
			statusSetBy: currentUser,
			statusSetOn: new Date(),
		},
	};
};

const setHandleToMenuAction = (args: TMenuAction, menuActions: Record<string, { disabled: boolean; handleOnClick: () => void }>) => {
	menuActions[MenuActionStatusKey.ApproveWashout].handleOnClick = () => {
		const { updateWashoutStatusMutation } = args;
		const input = defineInputToApprovement(args, "APPROVED");
		updateWashoutStatusMutation(input);
	};

	menuActions[MenuActionStatusKey.RetractApprovalWashout].handleOnClick = () => {
		const { updateWashoutStatusMutation } = args;
		const input = defineInputToApprovement(args, "RETRACTED");
		updateWashoutStatusMutation(input);
	};

	menuActions[MenuActionStatusKey.GenerateWashout].handleOnClick = () => {
		const { generateWashoutMutation } = args;
		generateWashoutMutation();
	};
};
const getMenuActions = (params: TMenuAction) => {
	const { statusKey, isPendingUpdateWashout, isPendingGenerateWashout, disabledMenuAction } = params;
	const { shouldDisabledApproveWashoutMenu, shouldDisabledRetractWashoutMenu, shouldDisabledGenerateWashoutMenu } = disabledMenuAction;
	const instanceMenuActions = cloneDeep(MenuActions);
	setHandleToMenuAction(params, instanceMenuActions);
	switch (statusKey) {
		case StatusKey.RetractWashout: {
			instanceMenuActions[MenuActionStatusKey.GenerateWashout].disabled = or(
				isPendingUpdateWashout,
				shouldDisabledGenerateWashoutMenu
			);
			instanceMenuActions[MenuActionStatusKey.ApproveWashout].disabled = or(isPendingUpdateWashout, shouldDisabledApproveWashoutMenu);
			instanceMenuActions[MenuActionStatusKey.RetractApprovalWashout].disabled = or(
				shouldDisabledRetractWashoutMenu,
				statusKey === StatusKey.RetractWashout
			);
			return instanceMenuActions;
		}
		case StatusKey.ApprovedWashout: {
			instanceMenuActions[MenuActionStatusKey.GenerateWashout].disabled = or(
				isPendingUpdateWashout,
				shouldDisabledGenerateWashoutMenu
			);
			instanceMenuActions[MenuActionStatusKey.ApproveWashout].disabled = or(
				shouldDisabledApproveWashoutMenu,
				statusKey === StatusKey.ApprovedWashout
			);
			instanceMenuActions[MenuActionStatusKey.RetractApprovalWashout].disabled = or(
				isPendingUpdateWashout,
				shouldDisabledRetractWashoutMenu
			);
			return instanceMenuActions;
		}
		case StatusKey.NoPayPlan: {
			instanceMenuActions[MenuActionStatusKey.GenerateWashout].disabled = or(
				isPendingUpdateWashout,
				shouldDisabledGenerateWashoutMenu
			);
			instanceMenuActions[MenuActionStatusKey.ApproveWashout].disabled = shouldDisabledApproveWashoutMenu;
			instanceMenuActions[MenuActionStatusKey.RetractApprovalWashout].disabled = shouldDisabledRetractWashoutMenu;
			return instanceMenuActions;
		}
		case StatusKey.NoWashout:
		case StatusKey.FailWashout:
		case StatusKey.NoPayPlanSigned: {
			instanceMenuActions[MenuActionStatusKey.GenerateWashout].disabled = or(
				isPendingGenerateWashout,
				shouldDisabledGenerateWashoutMenu
			);
			instanceMenuActions[MenuActionStatusKey.ApproveWashout].disabled = shouldDisabledApproveWashoutMenu;
			instanceMenuActions[MenuActionStatusKey.RetractApprovalWashout].disabled = shouldDisabledRetractWashoutMenu;
			return instanceMenuActions;
		}
		default:
			return instanceMenuActions;
	}
};

const PositionTreeMenuActions = (props: PositionTreeMenuActionsProps) => {
	const { anchorEl, setAnchorEl, menuActions } = props;
	const showMenu = Boolean(anchorEl);

	const handleClose = (event: React.MouseEvent<HTMLElement, BackdropClassKey> | React.KeyboardEvent<HTMLElement>) => {
		event.stopPropagation();
		setAnchorEl(null);
	};

	const handleApproveWashout = (event?: React.MouseEvent<HTMLElement>) => {
		event?.stopPropagation();
		menuActions?.[MenuActionStatusKey.ApproveWashout]?.handleOnClick();
		setAnchorEl(null);
	};
	const handleRetractApproval = (event?: React.MouseEvent<HTMLElement>) => {
		event?.stopPropagation();
		menuActions?.[MenuActionStatusKey.RetractApprovalWashout]?.handleOnClick();
		setAnchorEl(null);
	};

	const handleGenerateWashout = (event?: React.MouseEvent<HTMLElement>) => {
		event?.stopPropagation();
		menuActions?.[MenuActionStatusKey.GenerateWashout]?.handleOnClick();
		setAnchorEl(null);
	};

	const actions = useMemo(
		() => [
			{
				label: "Approve Washout",
				key: MenuActionStatusKey.ApproveWashout,
				disabled: menuActions ? menuActions[MenuActionStatusKey.ApproveWashout]?.disabled : false,
				onClick: handleApproveWashout,
			},
			{
				label: "Retract Approval",
				key: MenuActionStatusKey.RetractApprovalWashout,
				disabled: menuActions ? menuActions[MenuActionStatusKey.RetractApprovalWashout]?.disabled : false,
				onClick: handleRetractApproval,
			},
			{
				label: "Generate Washout",
				key: MenuActionStatusKey.GenerateWashout,
				disabled: menuActions ? menuActions[MenuActionStatusKey.GenerateWashout]?.disabled : false,
				onClick: handleGenerateWashout,
			},
		],
		[menuActions]
	);

	return (
		<Menu
			id="basic-menu"
			anchorEl={anchorEl}
			open={showMenu}
			onClose={handleClose}
			TransitionComponent={Fade}
			anchorOrigin={{
				vertical: "bottom",
				horizontal: "right",
			}}
			transformOrigin={{
				vertical: "top",
				horizontal: "right",
			}}
			MenuListProps={{
				"aria-labelledby": "basic-menu",
			}}
		>
			{actions.map((action) => (
				<MenuItem key={action.key} onClick={action.onClick} disabled={action.disabled}>
					<Typography className="!font-sans !text-[14px]" sx={{ color: "#666666" }} component="span">
						{action.label}
					</Typography>
				</MenuItem>
			))}
		</Menu>
	);
};

const PositionTreeMenu = (props: PositionTreeMenuProps) => {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const {
		disabled = false,
		shouldShowMenu = false,
		classes = "",
		selected = false,
		locationId,
		currentUser,
		item,
		position,
		itemProps,
		unReviewable,
		disabledMenuAction,
	} = props;

	const showMenuOptions = Boolean(anchorEl);

	const { commissionCycleId, payPeriodId, userId } = item;

	const { useWashoutQueries } = useFactoryWashout();
	const selectedPosition = normalizePositions(position || "");
	const washoutQueries = useWashoutQueries(selectedPosition) as unknown as IWashoutQueries;
	const { updateWashoutStatusMutation, isPendingUpdateWashout, generateWashoutMutation, isPendingGenerateWashout } =
		useWashoutReviewActions({
			locationId,
			commissionCycleId,
			washoutQueries,
			position,
			payPeriodId,
			userId,
		});

	if (!shouldShowMenu) {
		return null;
	}
	const condition = evaluatePositionStatusConditions({ itemProps, unReviewable });
	if (!condition?.key) {
		return null;
	}
	const menuActions = getMenuActions({
		item,
		locationId,
		currentUser,
		updateWashoutStatusMutation,
		isPendingUpdateWashout,
		generateWashoutMutation,
		isPendingGenerateWashout,
		disabledMenuAction,
		statusKey: condition?.key,
		isSelected: selected ?? false,
	});

	const handleOnClick = (event: React.MouseEvent<HTMLElement>) => {
		if (disabled) {
			return;
		}
		event.stopPropagation();
		setAnchorEl(event.currentTarget);
	};
	return (
		<Stack>
			<IconButton
				onClick={handleOnClick}
				hidden={!showMenuOptions}
				disabled={disabled}
				id="basic-button"
				aria-controls={showMenuOptions ? "basic-menu" : undefined}
				aria-haspopup="true"
				sx={{ minWidth: 16, maxWidth: 16, minHeight: 16, maxHeight: 16 }}
				aria-expanded={showMenuOptions ? "true" : undefined}
				className={clsx({
					["bg-disabled"]: true,
					[classes]: Boolean(classes),
				})}
			>
				{!selected ? (
					<TreeIcon icon={PositionMenu} sx={ICON_SX}></TreeIcon>
				) : (
					<TreeIcon icon={PositionMenuSelected} sx={ICON_SX}></TreeIcon>
				)}
			</IconButton>
			<PositionTreeMenuActions setAnchorEl={setAnchorEl} anchorEl={anchorEl} menuActions={menuActions} />
		</Stack>
	);
};

export default PositionTreeMenu;
